/**
 * Centralized date formatting utilities for CommodityBook
 * This ensures consistent date display across the entire application.
 *
 * Standard format: "26 Nov 2025, 09:55" (dd MMM yyyy, HH:mm)
 */

/**
 * Format a date for display in the UI with time
 * @example "26 Nov 2025, 09:55"
 */
export const formatDateTime = (date: Date | string | null | undefined): string => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) return '';

    return dateObj.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
};

/**
 * Format a date for display in the UI without time
 * @example "26 Nov 2025"
 */
export const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) return '';

    return dateObj.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

/**
 * Format a date for use in HTML date input fields (YYYY-MM-DD)
 * @example "2025-11-26"
 */
export const formatDateForInput = (date: Date | string | null | undefined): string => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) return '';

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

/**
 * Parse a date string from HTML date input (YYYY-MM-DD) to Date object
 */
export const parseDateFromInput = (dateString: string): Date | null => {
    if (!dateString) return null;

    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
};
